// dbProxy - usar cliente único de config/supabase v3
import { supabase } from '../config/supabase';

class DBQuery {
  constructor(table) {
    this._table = table;
    this._op = 'select';
    this._select = '*';
    this._filters = [];
    this._order = null;
    this._limit = null;
    this._offset = null;
    this._single = false;
    this._data = null;
    this._postSelect = null;
  }

  select(cols = '*') {
    if (this._op === 'insert' || this._op === 'update') {
      this._postSelect = cols;
      return this;
    }
    this._op = 'select';
    this._select = cols;
    return this;
  }
  
  single() { this._single = true; return this; }
  maybeSingle() { this._single = true; return this; }

  insert(data) { this._op = 'insert'; this._data = data; return this; }
  upsert(data, opts = {}) { this._op = 'upsert'; this._data = data; this._upsertOpts = opts; return this; }
  update(data) { this._op = 'update'; this._data = data; return this; }
  delete() { this._op = 'delete'; return this; }

  eq(col, val) { this._filters.push({ col, val }); return this; }
  neq(col, val) { this._filters.push({ col, val, type: 'neq' }); return this; }
  gt(col, val) { this._filters.push({ col, val, type: 'gt' }); return this; }
  lt(col, val) { this._filters.push({ col, val, type: 'lt' }); return this; }
  gte(col, val) { this._filters.push({ col, val, type: 'gte' }); return this; }
  lte(col, val) { this._filters.push({ col, val, type: 'lte' }); return this; }
  in(col, val) { this._filters.push({ col, val: Array.isArray(val) ? val : [val], type: 'in' }); return this; }

  order(col, opts = {}) { this._order = { col, ascending: opts.ascending !== false }; return this; }
  limit(n) { this._limit = n; return this; }
  range(from, to) { this._offset = from; this._limit = to - from + 1; return this; }

  _applyFiltersToQuery(query) {
    console.log('[dbProxy] _applyFiltersToQuery START:', { 
      filters: this._filters, 
      queryConstructor: query.constructor.name,
      queryType: typeof query,
      hasEq: typeof query?.eq,
      hasNeq: typeof query?.neq
    });
    for (const f of this._filters) {
      const col = f.col;
      const val = f.val;
      const type = f.type || 'eq';
      console.log('[dbProxy] Applying filter:', { col, val, type });
      switch (type) {
        case 'eq': query = query.eq(col, val); break;
        case 'neq': query = query.neq(col, val); break;
        case 'gt': query = query.gt(col, val); break;
        case 'lt': query = query.lt(col, val); break;
        case 'gte': query = query.gte(col, val); break;
        case 'lte': query = query.lte(col, val); break;
        case 'in': query = query.in(col, val); break;
      }
    }
    console.log('[dbProxy] _applyFiltersToQuery END, queryType:', typeof query);
    return query;
  }

  async execute() {
    console.log('[dbProxy] execute:', { op: this._op, table: this._table, filters: this._filters, data: this._data });
    try {
      // INSERT
      if (this._op === 'insert') {
        console.log('[dbProxy] INSERT operation');
        let query = supabase.from(this._table).insert(this._data);
        if (this._postSelect) query = query.select(this._postSelect);
        let result = await query;
        console.log('[dbProxy] INSERT result type:', typeof result, 'keys:', result ? Object.keys(result) : 'none');
        console.log('[dbProxy] INSERT result full:', result);
        if (result.error) {
          console.error('[dbProxy] INSERT error:', result.error);
          return { data: null, error: result.error.message };
        }
        if (this._single && result.data) result.data = result.data[0];
        return { data: result.data, error: null };
      }

      // UPSERT
      if (this._op === 'upsert') {
        console.log('[dbProxy] UPSERT operation');
        let query = supabase.from(this._table).upsert(this._data, this._upsertOpts || {});
        if (this._postSelect) query = query.select(this._postSelect);
        let result = await query;
        if (result.error) {
          console.error('[dbProxy] UPSERT error:', result.error);
          return { data: null, error: result.error.message };
        }
        if (this._single && result.data) result.data = result.data[0];
        return { data: result.data, error: null };
      }
      
      // UPDATE
      if (this._op === 'update') {
        console.log('[dbProxy] UPDATE operation');
        if (this._filters.length === 0) {
          return { data: null, error: 'UPDATE requires a WHERE clause' };
        }
        let query = supabase.from(this._table).update(this._data);
        console.log('[dbProxy] After update(), query:', typeof query);
        query = this._applyFiltersToQuery(query);
        console.log('[dbProxy] After filters, query:', typeof query);
        if (this._postSelect) query = query.select(this._postSelect);
        let result = await query;
        if (result.error) return { data: null, error: result.error.message };
        if (this._single && result.data) result.data = result.data[0];
        return { data: result.data, error: null };
      }
      
      // DELETE
      if (this._op === 'delete') {
        console.log('[dbProxy] DELETE operation');
        if (this._filters.length === 0) {
          return { data: null, error: 'DELETE requires a WHERE clause' };
        }
        let query = supabase.from(this._table).delete();
        query = this._applyFiltersToQuery(query);
        let result = await query;
        if (result.error) return { data: null, error: result.error.message };
        return { data: result.data, error: null };
      }
      
      // SELECT
      console.log('[dbProxy] SELECT operation');
      let query = supabase.from(this._table).select(this._select);
      query = this._applyFiltersToQuery(query);
      
      if (this._order) query = query.order(this._order.col, { ascending: this._order.ascending });
      if (this._limit) query = query.limit(this._limit);
      if (this._offset) query = query.range(this._offset, this._offset + (this._limit || 20) - 1);

      let result = this._single ? await query.maybeSingle() : await query;
      
      if (result.error) return { data: null, error: result.error.message };
      
      const data = this._single ? (result.data?.[0] || null) : result.data;
      return { data, error: null };
    } catch (err) {
      console.error('[dbProxy] Error:', err.message, err.stack);
      return { data: null, error: err.message };
    }
  }
}

const db = (table) => new DBQuery(table);
export default db;